import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setAuthInfo, setLoadingAuth } from "../userSlice";
import { clearDietaryRestrictions, selectProducts } from "../../products/productsSlice";
import { loadAffiliate, loadLogoUrl, resetReferrals, selectAffiliate, setEmbedded } from "../../referrals/referralsSlice";
import { loadCart, selectCartCoupon, selectCartItems, setCartCoupon, setCartItem } from "../../cart/cartSlice";
import { selectVisitorID, setGoogleLoginError, setVisitorID } from "../../mobile/mobileSlice";
import { getCouponByCode } from "../../../API/coupons";
import UserAPI from "../../../API/userAPI";
import CouponObj from "../../coupons/couponObj";
import CartObj from "../../cart/cartObj";
import CustomerObj from "../../customer/customerObj";
import { selectCustomer } from "../../customer/customerSlice";
import AffiliateObj from "../../referrals/AffiliateObj";
import Loader from "../../../components/Loader";

const GoogleCallback = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const cartCoupon = useSelector(selectCartCoupon);
  const products = useSelector(selectProducts);
  const customer = new CustomerObj(useSelector(selectCustomer));
  const affiliateData = useSelector(selectAffiliate);
  const affiliateVisitorID = useSelector(selectVisitorID);
  const affiliateSavedData = new AffiliateObj(affiliateData);
  const affiliateMeta = affiliateSavedData?.data;

  const finalRedirect = (success: boolean, token?: string, email?: string, error?: string) => {
    const params = new URLSearchParams({
      status: success ? "success" : "error",
      ...(token && { token }),
      ...(email && { email }),
      ...(error && { error })
    }).toString();

    const universalLink = `${process.env.REACT_APP_WEB_URL}auth/finish?${params}`;

    window.location.replace(universalLink);

    setTimeout(() => {
      if (window.opener) window.close();
    }, 2000);
  };

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = hashParams.get("access_token");

    if (!accessToken) {
      finalRedirect(false, undefined, undefined, "missing_token");
      return;
    }

    axios.get(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${accessToken}`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    })
      .then((res) => handleGoogleResolve(res.data, accessToken))
      .catch(() => finalRedirect(false, undefined, undefined, "google_api_error"));
    // eslint-disable-next-line
  }, []);

  const getCouponCodeFromQuery = (token: any, email: any, couponCode: any) => {
    getCouponByCode(couponCode).then((coupons) => {
      if (!coupons.length) {
        dispatch(setGoogleLoginError(`Coupon "${couponCode}" is invalid.`));
        return;
      }
      const coupon = new CouponObj(coupons[0]);
      const validation = coupon.validate(email, customer, new CartObj(cartItems), products);
      if (validation.error) {
        dispatch(setGoogleLoginError(validation.message));
        return;
      }
      dispatch(setCartCoupon({ token, coupon: coupons[0] }));
    });
  };

  const handleGoogleResolve = async (userData: any, accessToken: any) => {
    await UserAPI.socialLogin(userData.email, userData.family_name, userData.given_name)
      .then(async (response) => {
        if (response?.data?.status === 403) {
          dispatch(setGoogleLoginError("Invalid credentials"));
          finalRedirect(false, undefined, userData.email, "invalid_credentials");
          return;
        }
        if (response?.code) {
          finalRedirect(false, undefined, userData.email, "server_error");
          return;
        }

        const setLoginInfo = {
          token: response.token,
          email: response.user_email,
          nicename: response.user_nicename,
          display_name: response.user_display_name,
          isRememberMe: false,
          dateStored: new Date()
        };
        localStorage.setItem("token", response.token);
        localStorage.setItem("setAuthInfo", JSON.stringify(setLoginInfo));

        dispatch(clearDietaryRestrictions());
        dispatch(setAuthInfo(setLoginInfo));
        dispatch(resetReferrals());

        Object.entries(cartItems).forEach(([, item]: any) => {
          dispatch(setCartItem({
            token: response.token,
            cart_item: {
              product_id: item.product_id,
              product_qty: item.product_qty,
              product_price: item.product_price
            }
          }));
        });
        await dispatch(loadCart(response.token));

        getCouponCodeFromQuery(response.token, response.user_email, cartCoupon?.code);

        if (affiliateMeta?.meta?.custom_slug?.length) {
          dispatch(loadLogoUrl(affiliateSavedData.getCustomSlug()));
          dispatch(setEmbedded(true));
          if (affiliateVisitorID) dispatch(setVisitorID(`${affiliateVisitorID}`));
          if (affiliateMeta.affiliate_id) dispatch(loadAffiliate(affiliateMeta.affiliate_id));
        }

        finalRedirect(true, response.token, response.user_email);
      })
      .catch(() => finalRedirect(false, undefined, userData.email, "network_error"))
      .finally(() => dispatch(setLoadingAuth(false)));
  };

  return <Loader />;
};

export default GoogleCallback;